import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Download, Flame, PackageCheck, RefreshCw, Zap } from "lucide-react";
import { api, money, errMsg } from "@/lib/api";

export function AdminCJ({ onDone }) {
  const [connected, setConnected] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [trending, setTrending] = useState(false);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState("");
  const [importing, setImporting] = useState("");

  const check = () => api.get("/admin/cj/status")
    .then((r) => setConnected(r.data.connected === true))
    .catch(() => {
      // The backend may still be waking up (cold start) — retry once before
      // ever claiming "not connected", and only trust an explicit connected:false.
      setTimeout(() => api.get("/admin/cj/status")
        .then((r) => setConnected(r.data.connected === true))
        .catch(() => setConnected("unreachable")), 4000);
    });
  useEffect(() => { check(); }, []);

  const search = async (e, isTrending = false) => {
    e?.preventDefault();
    setBusy("search");
    try {
      const { data } = await api.post("/admin/cj/search", isTrending ? { trending: true } : { keyword });
      setResults(Array.isArray(data) ? data : []);
      setTrending(isTrending);
      if (!data.length) toast(isTrending ? "No trending picks right now" : "No CJ products found for that search");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
  };

  const importP = async (r) => {
    setImporting(r.pid);
    try {
      const { data } = await api.post("/admin/cj/import", { pid: r.pid, markup: 2.2, category: "Trending" });
      toast(`Added: ${data.name} at ${money(data.price)}`);
      onDone?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setImporting(""); }
  };

  const importTrending = async () => {
    setBusy("trending");
    try {
      await api.post("/admin/cj/import-trending", { count: 10 });
      toast("Import started — 10 trending products will appear in about a minute");
      setTimeout(() => onDone?.(), 45000);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
  };

  const testOrder = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/admin/cj/test-order");
      toast.success(data.cj_order_id ? `Sandbox order created in CJ — id ${data.cj_order_id}` : "Sandbox order submitted to CJ");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
  };

  const syncStock = async () => {
    setBusy("stock");
    try {
      await api.post("/admin/cj/sync-stock");
      toast("Stock sync started — low-stock products hide automatically");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="admin-cj">
      {connected === null && (
        <p className="mb-4 rounded-xl border border-[var(--line)] p-4 text-sm text-[var(--ink-soft)]" data-testid="admin-cj-checking">
          Checking CJ connection…
        </p>
      )}
      {connected === "unreachable" && (
        <p className="mb-4 rounded-xl border border-[var(--line)] p-4 text-sm text-[var(--ink-soft)]" data-testid="admin-cj-unreachable">
          Can't read the CJ connection status right now — refresh in a minute and it will settle.
        </p>
      )}
      {connected === false && (
        <p className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="admin-cj-not-connected">
          Not connected yet: add your CJ API key as the <strong>CJ_API_KEY</strong> secret, then re-publish.
        </p>
      )}
      <form onSubmit={(e) => search(e, false)} className="flex flex-wrap gap-2">
        <input
          value={keyword} onChange={(e) => setKeyword(e.target.value)}
          placeholder="Search CJ catalog e.g. wireless headphones"
          className="h-11 min-w-0 flex-1 rounded-full border border-[var(--line)] bg-white/60 px-5 text-sm outline-none focus:border-[var(--terra)]"
          data-testid="admin-cj-search-input"
        />
        <button disabled={busy === "search"} className="flex items-center gap-2 rounded-full bg-[var(--ink)] px-5 text-sm font-semibold text-[var(--bone)]" data-testid="admin-cj-search-button">
          <Search size={16} /> {busy === "search" ? "Searching…" : "Search"}
        </button>
        <button type="button" onClick={(e) => search(e, true)} disabled={busy === "search"} className="flex items-center gap-2 rounded-full border border-[var(--terra)] px-5 text-sm font-semibold text-[var(--terra)]" data-testid="admin-cj-trending-button">
          <Flame size={16} /> Trending now
        </button>
      </form>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={importTrending} disabled={busy === "trending"} className="flex items-center gap-2 rounded-full bg-[var(--terra)] px-4 py-2.5 text-xs font-semibold text-[#241a06]" data-testid="admin-cj-import-trending-button">
          <Zap size={14} /> {busy === "trending" ? "Importing…" : "Import 10 trending"}
        </button>
        <button onClick={testOrder} disabled={busy === "test"} className="flex items-center gap-2 rounded-full border border-[var(--line)] px-4 py-2.5 text-xs font-semibold" data-testid="admin-cj-test-order-button">
          <PackageCheck size={14} /> {busy === "test" ? "Testing…" : "Test order (sandbox)"}
        </button>
        <button onClick={syncStock} disabled={busy === "stock"} className="flex items-center gap-2 rounded-full border border-[var(--line)] px-4 py-2.5 text-xs font-semibold" data-testid="admin-cj-sync-stock-button">
          <RefreshCw size={14} /> {busy === "stock" ? "Syncing…" : "Sync stock"}
        </button>
      </div>
      {trending && results?.length > 0 && (
        <p className="mt-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--terra)]" data-testid="admin-cj-trending-label">
          <Flame size={14} /> CJ's hottest products right now
        </p>
      )}
      <div className="mt-6 space-y-2">
        {(results || []).map((r) => (
          <div key={r.pid} className="flex items-center gap-4 rounded-2xl border border-[var(--line)] p-3" data-testid={`admin-cj-row-${r.pid}`}>
            <img src={r.image || "/img/volt.jpg"} alt={r.name} className="h-14 w-12 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{r.name}</p>
              <p className="text-xs text-[var(--ink-soft)]">{r.price ? `Supplier price: ${money(r.price)} · your price ≈ ${money(r.price * 2.2 + 0.99)}` : "Tap Add to store to see the live supplier price"}</p>
            </div>
            <button
              onClick={() => importP(r)} disabled={importing === r.pid}
              className="flex shrink-0 items-center gap-2 rounded-full bg-[var(--terra)] px-4 py-2.5 text-xs font-semibold text-[#241a06]"
              data-testid={`admin-cj-import-${r.pid}`}
            >
              <Download size={14} /> {importing === r.pid ? "Adding…" : "Add to store"}
            </button>
          </div>
        ))}
        {results && results.length === 0 && <p className="py-8 text-center text-[var(--ink-soft)]">Nothing found — try different words.</p>}
      </div>
    </div>
  );
}
